import React from 'react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import {
	Checkbox,
	MessageBar,
	MessageBarType,
	ProgressIndicator,
	Stack,
	StackItem,
	TextField,
	TooltipHost,
} from '@fluentui/react';
import { IDragDroppedFile, IOutlookAppCommands, IOutlookItemInfo } from '../state/CorporateArchiveStates/LegalEntityDocumentsStateOutlook';
import LegalEntityDocument from './LegalEntityDocument';
import {
	EntityAPI,
	IEmailFilingRequest,
	IEntityTypeUserInfo,
	IFilingRequestBase,
	IFilingResult,
	IUserConfigPackage,
} from '../API';

export interface IDocumentUploadProps {
	state: DocumentUploadState;
}

class DocumentUpload extends React.Component<IDocumentUploadProps, {}> {
	componentDidMount(): void { }

	render(): React.ReactNode {
		return (
			<Stack>
				<Stack horizontal>
					<div style={{ marginRight: 10 }}>
						{' '}
						{LegalEntityDocument.getDocumentIcon(this.props.state.itemName + this.props.state.extension)}
					</div>

					<Checkbox
						checked={this.props.state.selected}
						onChange={(_, checked) => {
							this.props.state.setSelected(!!checked);
						}}
					/>
					<TooltipHost content="Click to rename" styles={{ root: { flexGrow: 1 } }}>
						<TextField
							readOnly={!this.props.state.isEditingName}
							value={this.props.state.baseName}
							onClick={() => this.props.state.setEditingName(true)}
							onBlur={() => this.props.state.setEditingName(false)}
							onChange={(_, value) => {
								this.props.state.setBaseName(value || '');
							}}
							styles={{
								root: { flexGrow: 1, marginLeft: 8, marginTop: -8 },
								field: {
									fontSize: 12,
									cursor: this.props.state.isEditingName ? 'text' : 'pointer',
									backgroundColor: this.props.state.isEditingName ? undefined : 'transparent',
								},
								fieldGroup: this.props.state.isEditingName ? undefined : {
									border: '1px solid transparent',
									backgroundColor: 'transparent',
									'&:hover': { border: '1px solid #c8c6c4' },
								},
							}}
						/>
					</TooltipHost>
					{this.props.state.extension && (
						<span style={{ fontSize: 12, alignSelf: 'center', whiteSpace: 'nowrap', color: '#605e5c', marginTop: -7 }}>
							{this.props.state.extension}
						</span>
					)}
				</Stack>

				{ this.props.state.isUploading && this.props.state.uploadProgressPercent === 0 && (
					<ProgressIndicator />
				)}

				{this.props.state.isUploading && this.props.state.uploadProgressPercent > 0 && (
					<Stack horizontal>
						<StackItem grow>
							<ProgressIndicator
								percentComplete={this.props.state.uploadProgressPercent}
							/>
						</StackItem>
						<StackItem>
							<span>{this.props.state.uploadProgressPercent}%</span>
						</StackItem>
					</Stack>
				)}

				{this.props.state.error ? (
					<MessageBar messageBarType={MessageBarType.error}>
						{this.props.state.error}
					</MessageBar>
				) : (
					<></>
				)}
			</Stack>
		);
	}
}

export default observer(DocumentUpload);

export class DocumentUploadState {
	private commands: IOutlookAppCommands;
	public userConfig: IUserConfigPackage;
	public attachment?: Office.AttachmentDetails;

	public selected: boolean = true;

	public uploadProgressPercent: number = 0;
	public isUploading:boolean = false;
	public entityInfo?: IEntityTypeUserInfo;

	public uploadAndCreateNewEntity: boolean = false;

	public itemName: string = '';
	public isEditingName: boolean = false;
	private _extension: string = '';

	private url: string;
	private entityId: string;
	message?: IOutlookItemInfo;
	file?: IDragDroppedFile;

	constructor(
		entityType: IEntityTypeUserInfo | undefined,
		entityId: string,
		url: string,
		userConfig: IUserConfigPackage,
		commands: IOutlookAppCommands
	) {
		this.userConfig = userConfig;
		this.commands = commands;
		this.entityInfo = entityType;
		this.url = url;
		this.entityId = entityId;

		makeAutoObservable(this);
	}

	private setIsUploading(val:boolean) {
		this.isUploading = val;
	}

	private static splitName(fullName: string, attachment: Office.AttachmentDetails | undefined): { base: string; ext: string } {
		const dot = fullName.lastIndexOf('.');
		let result = dot > 0
			? { base: fullName.slice(0, dot), ext: fullName.slice(dot) }
			: { base: fullName, ext: '' };

		if(!result.ext && attachment?.attachmentType === 'item') {
			result.ext = '.eml';
		}

		return result;
	}

	public async setSelectedItemFromFile(file: IDragDroppedFile) {
		this.attachment = undefined;
		this.message = undefined;

		//We may be able to set the related message, if the attachment belongs to the active message.
		//It's possible to drag a file into upload area that is not related to it so we have to check for that.
		//In future, it would be better if the drag/drop event supplied the message id.
		let selectedMsg = await this.commands.getActiveItemInfo();
		if(selectedMsg) {
			let selectedMsgAttachments = await this.commands.getAttachments();
			let matchingAttachment = selectedMsgAttachments?.find(a => a.name === file.name);
			if(matchingAttachment) {
				this.setSelectedItemFromMessage(selectedMsg, matchingAttachment);
				return;
			}
		}

		this.file = file;
		const { base, ext } = DocumentUploadState.splitName(file.name, undefined);
		this.itemName = base;
		this._extension = ext;
	}

	public setSelectedItemFromMessage(message?: IOutlookItemInfo, attachment?: Office.AttachmentDetails) {
		this.message = message;
		this.attachment = attachment;
		const fullName = attachment?.name || (message?.subject + '.eml');
		const { base, ext } = DocumentUploadState.splitName(fullName, attachment);

		this.itemName = base;
		this._extension = ext;
	}

	public get extension(): string {
		return this._extension;
	}

	public get baseName(): string {
		return this.itemName;
	}

	public setItemName(itemName: string) {
		const { base, ext } = DocumentUploadState.splitName(itemName, undefined);
		this.itemName = base;
		this._extension = ext;
	}

	public setBaseName(baseName: string) {
		this.itemName = baseName;
	}

	public setEditingName(editing: boolean) {
		this.isEditingName = editing;
	}

	public setSelected(selected: boolean) {
		this.selected = selected;
	}

	public setUploadProgress(percent: number) {
		this.uploadProgressPercent = percent;
	}

	public error: string = '';
	public setError(error: string) {
		this.error = error;
	}

	public setEntityId(id: string) {
		this.entityId = id;
	}

	public async upload(allowCreateNewEntity: boolean): Promise<IFilingResult> {
		this.setUploadProgress(0);
		this.setIsUploading(true);
		let api = EntityAPI.fromEntityTypeId(
			this.entityInfo?.id || '',
			await this.userConfig.getToken(),
		);
		let result: IFilingResult = { location: '', error: '' };

		let uploadData: IFilingRequestBase = {
			emailId: this.message?.id || '',
			attachmentId: this.attachment?.id || '',
			conversationId: this.message?.conversationId || '',
			internetMessageId: this.message?.internetMessageId || '',
			url: this.url,
			entityId: this.entityId,
			entityType: this.entityInfo?.id || '',
			createNewEntity: allowCreateNewEntity,
			senderEmail: Office.context.mailbox.userProfile.emailAddress,
			fileName: this.itemName + this._extension
		};

		//Simple case covering 95% of cases - there's an ID for the email and possibly attachment.
		if(uploadData.emailId) {
			let emailUpload = uploadData as IEmailFilingRequest;
			result = await api.saveEmailOrAttachmentById(emailUpload);
		} else if (this.attachment || this.file) {
			//The messy case we'd rather avoid - user is uploading a file we have no IDs for.

			let file: File | undefined;

			if (this.file) {
				//Contruct the file from the Blob fileContent
				file = new File([this.file.fileContent], this.itemName + this._extension);
			}

			if (!file && this.attachment) {

				let attachmentContents = await this.commands.getAttachmentContent(
					this.attachment.id,
				);
				if (attachmentContents) {
					let blob;
					try
					{
						blob = this.b64toBlob(attachmentContents.content);
					}
					catch (error)
					{
						console.error('Error converting base64 to blob:', error);

						//maybe it's a string and not base 64 - get the blob for the string
						blob = new Blob([attachmentContents.content], { type: 'application/octet-stream' });
					}

					if(blob.size) {
						file = new File([blob], this.itemName + this._extension);
					}
				}
			}

			if (file) {
				result = await api.saveAttachment(file, uploadData, (progress) => {
					this.setUploadProgress(progress);
				});
			}

		}

		if (result.error) {
			this.setError(result.error);
		}
		this.setIsUploading(false);
		return result;
	}

	private b64toBlob(b64Data: string, contentType = '', sliceSize = 512): Blob {
		const byteCharacters = atob(b64Data);
		const byteArrays = [];

		for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
			const slice = byteCharacters.slice(offset, offset + sliceSize);

			const byteNumbers = new Array(slice.length);
			for (let i = 0; i < slice.length; i++) {
				byteNumbers[i] = slice.charCodeAt(i);
			}

			const byteArray = new Uint8Array(byteNumbers);
			byteArrays.push(byteArray);
		}

		const blob = new Blob(byteArrays, { type: contentType });
		return blob;
	}
}
